import { useEffect, useRef, useState } from "react";
import WaveSurfer from "wavesurfer.js";
import RegionsPlugin from "wavesurfer.js/dist/plugins/regions.esm.js";
import { Play, Pause } from "lucide-react";
import { API } from "../lib/api";

const toSec = (t) => { const p = String(t || "0").split(":").map(Number); return p.length === 3 ? p[0] * 3600 + p[1] * 60 + p[2] : p.length === 2 ? p[0] * 60 + p[1] : p[0] || 0; };

// Scrubbable waveform with Track-ID markers. Media is fetched with credentials (auth-gated /api/files).
export function Waveform({ mediaUrl, tracklist = [], locked, onTime }) {
  const ref = useRef(null);
  const ws = useRef(null);
  const [ready, setReady] = useState(false);
  const [playing, setPlaying] = useState(false);
  const [err, setErr] = useState(null);
  const [time, setTime] = useState(0);

  useEffect(() => {
    if (!ref.current || !mediaUrl) return;
    let objUrl;
    const regions = RegionsPlugin.create();
    const w = WaveSurfer.create({ container: ref.current, waveColor: "#3F3F46", progressColor: "#E8552A", cursorColor: "#F4F4F5", height: 96, barWidth: 2, barGap: 1, barRadius: 2, normalize: true, plugins: [regions] });
    ws.current = w;
    w.on("ready", () => {
      setReady(true);
      if (!locked) tracklist.forEach((t, i) => regions.addRegion({ start: toSec(t.time), content: `${i + 1}. ${t.title}`, color: "rgba(232,85,42,0.9)", drag: false, resize: false }));
    });
    w.on("timeupdate", (s) => { setTime(s); onTime?.(s); }); w.on("play", () => setPlaying(true)); w.on("pause", () => setPlaying(false));
    w.on("error", () => setErr("Audio unavailable"));
    regions.on("region-clicked", (r, e) => { e.stopPropagation(); w.setTime(r.start); w.play(); });
    const src = mediaUrl.startsWith("/api/") ? `${API}${mediaUrl.slice(4)}` : mediaUrl;
    fetch(src, { credentials: "include" }).then(async (r) => { if (!r.ok) throw new Error(); objUrl = URL.createObjectURL(await r.blob()); w.load(objUrl); }).catch(() => setErr(src.includes("/files/") ? "Sign in to stream this set" : "Audio unavailable"));
    return () => { w.destroy(); if (objUrl) URL.revokeObjectURL(objUrl); };
  }, [mediaUrl, tracklist, locked]); // eslint-disable-line react-hooks/exhaustive-deps

  const fmt = (s) => `${Math.floor(s / 60)}:${String(Math.floor(s % 60)).padStart(2, "0")}`;
  return (
    <div data-testid="waveform" className="card-copper rounded-xl p-4">
      <div className="flex items-center gap-3 mb-3">
        <button data-testid="wave-play" disabled={!ready} onClick={() => ws.current?.playPause()} className="w-11 h-11 rounded-full copper-btn flex items-center justify-center disabled:opacity-40">{playing ? <Pause size={18} /> : <Play size={18} className="ml-0.5" />}</button>
        <span className="mono-label text-[#A1A1AA]">{fmt(time)}{ready && ws.current ? ` / ${fmt(ws.current.getDuration())}` : ""}</span>
        {err && <span className="mono-label text-[#FF9F1C] ml-auto">{err}</span>}
        {!err && !ready && <span className="mono-label text-[#71717A] ml-auto">DECODING WAVEFORM…</span>}
      </div>
      <div ref={ref} />
      {!locked && tracklist.length > 0 && (
        <div className="flex flex-wrap gap-2 mt-3">{tracklist.map((t, i) => <button key={i} data-testid={`marker-${i}`} onClick={() => { ws.current?.setTime(toSec(t.time)); ws.current?.play(); }} className="mono-label border border-[#27272A] rounded-full px-3 py-1 hover:border-[#E8552A] text-[#D4D4D8]"><span className="text-[#E8552A]">{t.time}</span> {t.title}</button>)}</div>
      )}
      {locked && tracklist !== null && <p className="mono-label text-[#71717A] mt-3">TRACK-ID MARKERS UNLOCK WITH PRIME</p>}
    </div>
  );
}
